import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import EditTagModal from '../components/EditTagModal';
import LoadingSpinner from '../components/LoadingSpinner';
import SingleTagQRScannerModal from '../components/SingleTagQRScannerModal';
// (2026-04-17, Jason Cox) R6 UHF hand scanner panel. A scanned tag populates
// the search term and triggers the same search flow used by QR scanning.
import RfidScannerPanel from '../components/RfidScannerPanel';
// (2026-04-17, Jason Cox) QRScannerButton removed from imports -- the small
// icon button next to the search input was replaced by the full-width
// "Camera" card in the side-by-side scan-input row.
import ManageTagsConfirmationModal, { OperationDetails, OperationType } from '../components/ManageTagsConfirmationModal';
import { TagMatch, Product } from '../types/index';
import { UnifiedTagInfo, UnifiedProduct } from '../services/unifiedApiService';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedApi } from '../hooks/useUnifiedApi';
import {
  TagIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PencilIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CameraIcon, // (2026-04-17, Jason Cox) Camera/QR option in the scan-input row
} from '@heroicons/react/24/outline';


const ManageTags: React.FC = () => {
  const { user } = useAuth();
  const api = useUnifiedApi();
  const location = useLocation();
  const [recentlyMatchedTags, setRecentlyMatchedTags] = useState<string[]>(location.state?.recentlyMatchedTags || []);
  const [tagMatches, setTagMatches] = useState<TagMatch[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredMatches, setFilteredMatches] = useState<TagMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Active search state (what's actually being applied)
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  // Edit modal state
  const [editingTag, setEditingTag] = useState<TagMatch | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Prevent duplicate calls
  const [isCurrentlyLoading, setIsCurrentlyLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTags, setTotalTags] = useState(0);
  const pageSize = 100; // Smaller page size for faster loading
  
  // QR Scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);
  
  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [operationDetails, setOperationDetails] = useState<OperationDetails | null>(null);

  useEffect(() => {
    // Only load data if we have search criteria
    if (hasSearched && activeSearchTerm) {
      loadData();
    }
  }, [currentPage, activeSearchTerm, hasSearched]);

  useEffect(() => {
    applyFilters();
  }, [tagMatches, activeSearchTerm]);

  // Clear recently matched tags after 30 seconds
  useEffect(() => {
    if (recentlyMatchedTags.length > 0) {
      const timer = setTimeout(() => {
        setRecentlyMatchedTags([]);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [recentlyMatchedTags.length]);

  /**
   * Enhanced error logging function
   */
  const logError = (operation: string, error: any, context: any) => {
    const errorLog = {
      timestamp: new Date().toISOString(),
      operation,
      user: user?.email || user?.username || 'unknown',
      error: error instanceof Error ? error.message : String(error),
      context,
      stack: error instanceof Error ? error.stack : undefined
    };
    
    // Log to console with structure
    console.error(`❌ ${operation} Failed:`, errorLog);
    
    // Store in localStorage for debugging (keep last 50 errors)
    try {
      const existingLogs = JSON.parse(localStorage.getItem('tagManagementErrors') || '[]');
      const updatedLogs = [errorLog, ...existingLogs].slice(0, 50);
      localStorage.setItem('tagManagementErrors', JSON.stringify(updatedLogs));
    } catch (e) {
      console.error('Failed to store error log:', e);
    }
  };

  const loadData = async () => {
    if (!api) {
      setError('API service not available');
      setLoading(false);
      return;
    }
    
    // Prevent duplicate calls
    if (isCurrentlyLoading) {
      return;
    }
    
    // Clear existing tags first to ensure no stale data remains
    setTagMatches([]);
    setFilteredMatches([]);
    
    setIsCurrentlyLoading(true);
    setLoading(true);
    try {
      // Use server-side search - pass the exact tag number
      const tagsResponse = await api.listRfidTags({ 
        page: currentPage,
        pageSize: pageSize,
        sortBy: 'latest',
        search: activeSearchTerm // Pass search term to API
      });
      
      // No products needed - SKU and Name come from warehouse data
      
      // Update pagination state
      setTotalTags(tagsResponse.total);
      setTotalPages(tagsResponse.totalPages);
      
      // Convert UnifiedTagInfo to TagMatch format for compatibility
      const convertedTags: TagMatch[] = tagsResponse.tags.map((tag: UnifiedTagInfo, index: number) => ({
        id: tag.id || tag.rfidNumber || `tag-${index}`,
        tagNumber: tag.rfidNumber || '',
        productId: tag.productSku || tag.productId || '', // Use SKU as productId for display
        status: tag.status,
        quantity: tag.quantity || 1,
        location: tag.location || undefined, // Initially empty or minimal
        smartAreaLocation: tag.smartAreaLocation || '',
        matchedAt: tag.matchedAt || new Date().toISOString(),
        matchedBy: tag.matchedBy || user?.id || '',
        // Store product info directly in the tag for easy access
        productName: tag.productName,
        productSku: tag.productSku
      }));
      
      // Set the tag data immediately (fast display)
      setTagMatches(convertedTags);
      setProducts([]); // Empty products - not needed on this page
      setError(null);
      setLoading(false); // Stop loading indicator - tags are displayed
      
      // Note: SWS warehouse endpoint already includes location data (whsName field)
      // No need for background location loading
      
      // Return the converted tags for immediate use
      return convertedTags;
      
    } catch (err) {
      console.error('Error loading tag data:', err);
      setError('Failed to load tag data');
      return [];
    } finally {
      setLoading(false);
      setIsCurrentlyLoading(false);
    }
  };


  const applyFilters = () => {
    let filtered = tagMatches;

    // Since we're using server-side search, we don't need to filter here
    // The API already returns only matching tags

    // Sort by matchedAt timestamp (descending - most recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.matchedAt).getTime();
      const dateB = new Date(b.matchedAt).getTime();
      
      // Primary sort by timestamp (descending)
      if (dateA !== dateB) {
        return dateB - dateA;
      }
      
      // Secondary sort by tag number (ascending) for tags matched at the same time
      return a.tagNumber.localeCompare(b.tagNumber);
    });

    setFilteredMatches(filtered);
  };

  const handleSearch = () => {
    // Apply the current search value
    setActiveSearchTerm(searchTerm);
    setHasSearched(true);
    setCurrentPage(1); // Reset to first page when searching
    
    // Force a reload of data even if search term hasn't changed
    // This ensures clicking search multiple times will re-execute the search
    if (searchTerm === activeSearchTerm && hasSearched) {
      // Force reload by calling loadData directly
      loadData();
    }
  };

  const handleClearSearch = () => {
    // Clear search and all results
    setSearchTerm('');
    setActiveSearchTerm('');
    setHasSearched(false);
    setCurrentPage(1);
    setTagMatches([]);
    setFilteredMatches([]);
  };
  
  const handleQRScanConfirmed = (tag: string) => {
    // Set the search term with the scanned tag
    setSearchTerm(tag);
    setShowQRScanner(false);
    
    // Clear previous results immediately
    setTagMatches([]);
    setFilteredMatches([]);
    
    // Auto-trigger search
    setActiveSearchTerm(tag);
    setHasSearched(true);
    setCurrentPage(1);
    // loadData() will be triggered by the useEffect
  };

  const handleEditTag = (tagMatch: TagMatch) => {
    setEditingTag(tagMatch);
    setShowEditModal(true);
  };

  // Handler for updating tag quantity only
  const handleUpdateQuantity = async (tagMatch: TagMatch, newQuantity: number) => {
    if (!user || !api) return;
    
    const oldQuantity = tagMatch.quantity;
    const product = getProductInfo(tagMatch.productId);
    
    setIsUpdating(true);
    try {
      // Log the operation start
      console.log('📝 Updating tag quantity:', {
        tagNumber: tagMatch.tagNumber,
        oldQuantity,
        newQuantity,
        product: product?.itemNumber
      });
      
      // Call the unified API to update tag quantity
      await api.updateTagQuantity(
        tagMatch.id, // Tag ID
        tagMatch.tagNumber, // RFID number
        newQuantity // New quantity
      );
      
      console.log('✅ Tag quantity update API call successful');
      
      // Close modal first for better UX
      setShowEditModal(false);
      setEditingTag(null);
      
      // Refresh data from server to verify the update
      await loadData();
      
      // Prepare operation details for confirmation modal
      setOperationDetails({
        type: 'update_quantity',
        tagNumber: tagMatch.tagNumber,
        productSku: product?.itemNumber,
        productName: product?.description,
        oldValue: oldQuantity,
        newValue: newQuantity
      });
      
      // Show confirmation modal
      setShowConfirmationModal(true);
      
    } catch (err) {
      logError('Update Tag Quantity', err, {
        tagNumber: tagMatch.tagNumber,
        oldQuantity,
        newQuantity,
        productId: tagMatch.productId
      });
      setError('Failed to update tag quantity: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler for changing product association
  const handleUpdateProduct = async (tagMatch: TagMatch, newProductId: string, newQuantity: number) => {
    if (!user || !api) return;
    
    const oldProduct = getProductInfo(tagMatch.productId);
    // Note: We need to fetch the new product info
    const newProduct = products.find(p => p.id === newProductId) || { itemNumber: newProductId, description: 'Unknown Product' };
    
    setIsUpdating(true);
    try {
      // Call API to change product association
      console.log('📝 Changing product for tag:', tagMatch.tagNumber, 'to product ID:', newProductId);
      await api.changeTagProduct(tagMatch.tagNumber, newProductId);
      console.log('✅ Product change API call completed successfully');
      
      // If quantity also needs to be updated, do it separately
      if (newQuantity !== tagMatch.quantity) {
        console.log('📝 Also updating quantity from', tagMatch.quantity, 'to', newQuantity);
        await api.updateTagQuantity(tagMatch.id, tagMatch.tagNumber, newQuantity);
      }
      
      // Close modal first for better UX
      setShowEditModal(false);
      setEditingTag(null);
      
      // Refresh data from server to verify the update
      console.log('🔄 Refreshing tag data...');
      const refreshedTags = await loadData();
      console.log('✅ Data refresh completed');
      
      // Find the updated tag in the refreshed data to get the actual product info from API
      const updatedTag = refreshedTags?.find(t => t.tagNumber === tagMatch.tagNumber);
      const newProductSku = updatedTag?.productSku || newProductId;
      const newProductName = updatedTag?.productName || 'Unknown Product';
      
      // Prepare operation details for confirmation modal
      setOperationDetails({
        type: 'change_product',
        tagNumber: tagMatch.tagNumber,
        oldProductSku: oldProduct?.itemNumber,
        oldProductName: oldProduct?.description,
        newProductSku: newProductSku,
        newProductName: newProductName
      });
      
      // Show confirmation modal
      setShowConfirmationModal(true);
      
    } catch (err) {
      logError('Change Tag Product', err, {
        tagNumber: tagMatch.tagNumber,
        oldProductId: tagMatch.productId,
        newProductId,
        newQuantity
      });
      setError('Failed to update tag product: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler for clearing tag data (pending → not_matched)
  const handleClearTag = async (tagMatch: TagMatch) => {
    if (!user || !api) return;
    
    const product = getProductInfo(tagMatch.productId);
    
    setIsUpdating(true);
    try {
      console.log('🗑️ Clearing tag data:', {
        tagNumber: tagMatch.tagNumber,
        product: product?.itemNumber
      });
      
      // Call API to clear tag
      await api.clearTag(tagMatch.id, tagMatch.tagNumber);
      
      console.log('✅ Tag clear API call successful');
      
      // Close modal first for better UX
      setShowEditModal(false);
      setEditingTag(null);
      
      // Prepare operation details for confirmation modal
      setOperationDetails({
        type: 'clear_tag',
        tagNumber: tagMatch.tagNumber,
        productSku: product?.itemNumber,
        productName: product?.description
      });
      
      // Show confirmation modal
      setShowConfirmationModal(true);
      
      // Clear the search after showing confirmation
      // The tag is no longer in pending state
      
    } catch (err) {
      logError('Clear Tag', err, {
        tagNumber: tagMatch.tagNumber,
        tagId: tagMatch.id,
        productId: tagMatch.productId
      });
      setError('Failed to clear tag. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handler for replacing an RFID tag (old → abandon, new → pending)
  const handleReplaceTag = async (tagMatch: TagMatch, newTagNumber: string) => {
    if (!user || !api) return;
    
    const product = getProductInfo(tagMatch.productId);
    
    setIsUpdating(true);
    try {
      console.log('🔄 Replacing RFID tag:', {
        oldTag: tagMatch.tagNumber,
        newTag: newTagNumber,
        product: product?.itemNumber
      });
      
      // Call API to replace tag
      await api.replaceRfidTag(tagMatch.tagNumber, newTagNumber);
      
      console.log('✅ Tag replacement API call successful');
      
      // Close modal first for better UX
      setShowEditModal(false);
      setEditingTag(null);
      
      // Update search term to the new tag number
      setSearchTerm(newTagNumber);
      setActiveSearchTerm(newTagNumber);
      
      // Refresh data from server to show the new tag
      await loadData();
      
      // Prepare operation details for confirmation modal
      setOperationDetails({
        type: 'replace_tag',
        tagNumber: newTagNumber, // Show the new tag number
        oldValue: tagMatch.tagNumber,
        newValue: newTagNumber,
        productSku: product?.itemNumber,
        productName: product?.description
      });
      
      // Show confirmation modal
      setShowConfirmationModal(true);
      
    } catch (err) {
      logError('Replace Tag', err, {
        oldTagNumber: tagMatch.tagNumber,
        newTagNumber,
        tagId: tagMatch.id,
        productId: tagMatch.productId
      });
      setError('Failed to replace tag: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };


  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'not_matched':
        return <TagIcon className="h-4 w-4 text-blue-500" />;
      case 'on_hand':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'consumed':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case 'abandon':
        return <XMarkIcon className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'not_matched':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'on_hand':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'consumed':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'abandon':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-secondary-100 text-secondary-800`;
    }
  };

  const getProductInfo = (productId: string) => {
    // Find the tag that matches this productId (which is actually the SKU)
    const tag = tagMatches.find(t => t.productId === productId);
    if (!tag) return null;
    
    // Create a product object from the tag's embedded product info
    return {
      id: productId,
      itemNumber: tag.productSku || productId, // SKU
      description: tag.productName || 'Unknown Product', // Name from warehouse data
      category: '',
      manufacturer: '',
      model: '',
      specifications: {},
      status: 'active' as const,
      location: '',
      tags: []
    };
  };

  const canEditTag = (tagMatch: TagMatch) => {
    return tagMatch.status === 'pending' || tagMatch.status === 'not_matched' || tagMatch.status === 'on_hand';
  };

  /**
   * Handler for "Search More Tags" button in confirmation modal
   */
  const handleSearchMore = () => {
    // Clear search and reset to blank state
    setSearchTerm('');
    setActiveSearchTerm('');
    setHasSearched(false);
    setCurrentPage(1);
    setTagMatches([]);
    setFilteredMatches([]);
    setShowConfirmationModal(false);
    setOperationDetails(null);
  };

  /**
   * Handler for "View Updated Tag" button in confirmation modal
   */
  const handleViewUpdatedTag = () => {
    // Keep current search and refresh results
    setShowConfirmationModal(false);
    
    // If we cleared a tag, it won't be in results anymore
    // So we might want to clear the search
    if (operationDetails?.type === 'clear_tag') {
      // Clear search since the tag is no longer in pending state
      handleSearchMore();
    } else {
      // For other operations, refresh the current search
      if (hasSearched && activeSearchTerm) {
        loadData();
      }
    }
    
    setOperationDetails(null);
  };

  const uniqueLocations = Array.from(new Set([
    ...tagMatches.map(match => match.location).filter(Boolean),
    ...tagMatches.map(match => match.smartAreaLocation).filter(Boolean)
  ])).sort();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg"/>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="md:flex md:items-center md:justify-between mb-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-secondary-900">Manage Tags</h1>
              <p className="mt-1 text-sm text-secondary-600">
                View and manage your RFID tags.
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-medium text-secondary-900 mb-2">Search for RFID Tags</h3>
                  <p className="text-sm text-secondary-600">Scan a tag or enter a tag number below</p>
                </div>

                {/* (2026-04-17 / 2026-04-22 / 2026-04-24 / 2026-04-30,
                    Jason Cox) Scanner + Camera stay side-by-side at every
                    width so it's visually clear they're two alternative
                    inputs. The shared intro covers both -- the hand scanner
                    and the camera both read the QR / barcode labels.
                    Manual Entry sits below. All three route through
                    handleQRScanConfirmed / handleSearch so the scanned or
                    typed tag populates the search term and runs the
                    lookup. */}
                <p className="text-xs text-secondary-600">
                  Scan a single QR or barcode label with the RFID hand scanner or your device camera to look up the tag.
                </p>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <RfidScannerPanel
                    title="Hand Scanner"
                    onTagScanned={(epc) => handleQRScanConfirmed(epc)}
                  />
                  <div className="card">
                    <div className="card-body">
                      <h3 className="text-sm font-semibold text-secondary-900 mb-2">Camera</h3>
                      <button
                        type="button"
                        onClick={() => setShowQRScanner(true)}
                        className="btn-primary w-full flex flex-col items-center justify-center whitespace-nowrap"
                      >
                        <CameraIcon className="h-5 w-5 mb-1" />
                        <span>Start</span>
                        <span>Camera</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Manual search text input */}
                  <div className="flex-1">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchTerm.trim().length >= 3 && handleSearch()}
                        placeholder="Enter tag number (min. 3 characters)..."
                        className="block w-full pl-10 pr-10 py-2 border border-secondary-300 rounded-md leading-5 bg-white placeholder-secondary-500 focus:outline-none focus:placeholder-secondary-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          <XMarkIcon className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Search Button */}
                  <button
                    onClick={handleSearch}
                    disabled={searchTerm.trim().length < 3}
                    className={`px-4 py-2 font-medium text-sm rounded-md transition-colors ${
                      searchTerm.trim().length < 3
                        ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                    }`}
                  >
                    Search
                  </button>
                </div>
                {searchTerm.trim().length > 0 && searchTerm.trim().length < 3 && (
                  <div className="mt-2 text-sm text-secondary-500">
                    Please enter at least 3 characters to search
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Active Search */}
          {hasSearched && activeSearchTerm && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-secondary-600">Searching for:</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  "{activeSearchTerm}"
                </span>
                <button
                  onClick={handleClearSearch}
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Clear search
                </button>
              </div>
            </div>
          )}

          {/* Results Summary */}
          {hasSearched && (
            <div className="mb-4">
              <p className="text-sm text-secondary-600">
                {totalTags > 0 ? (
                  <>
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalTags)} of {totalTags.toLocaleString()} tags
                  </>
                ) : (
                  'No tags found'
                )}
              </p>
            </div>
          )}

          {/* Tags Display */}
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-md p-4 mb-4">
              <p className="text-sm text-error-700">{error}</p>
            </div>
          )}

          {!hasSearched ? null : filteredMatches.length === 0 ? (
            <div className="bg-white shadow rounded-lg">
              <div className="text-center py-12">
                <TagIcon className="mx-auto h-12 w-12 text-secondary-400" />
                <h3 className="mt-2 text-sm font-medium text-secondary-900">No tags found</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  No tags found matching "{activeSearchTerm}". Please check the tag number and try again.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="block lg:hidden space-y-4">
                {filteredMatches.map((match) => {
                  const product = getProductInfo(match.productId);
                  const isRecentlyMatched = recentlyMatchedTags.includes(match.tagNumber);
                  return (
                    <div key={match.id} className={`shadow rounded-lg border ${isRecentlyMatched ? 'bg-success-50 border-success-300' : 'bg-white border-secondary-200'}`}>
                      <div className="p-4">
                        {/* Header with Tag Number and Status */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <div className="mr-3">
                              {getStatusIcon(match.status)}
                            </div>
                            <div className="text-sm font-medium text-secondary-900 font-mono">
                              {match.tagNumber}
                            </div>
                          </div>
                          <span className={getStatusBadge(match.status)}>
                            {match.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>

                        {/* Product Info */}
                        <div className="mb-3">
                          <div className="text-sm font-medium text-secondary-900">
                            {product?.itemNumber || 'Unknown'}
                          </div>
                          <div className="text-sm text-secondary-500">
                            {product?.description || 'Product not found'}
                          </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-1 gap-4 mb-3 text-sm">
                          <div>
                            <span className="text-secondary-500 font-medium">Tag Package Quantity:</span>
                            <div className="text-secondary-900">{match.quantity}</div>
                          </div>
                        </div>

                        {/* Location Info and Action Button */}
                        <div className="flex items-end justify-between">
                          <div className="flex-1 mr-3">
                            <span className="text-secondary-500 font-medium text-sm">Location:</span>
                            {match.location ? (
                              <div className="text-sm text-secondary-900">{match.location}</div>
                            ) : (
                              <div className="text-sm text-secondary-500 italic">No location</div>
                            )}
                            {match.smartAreaLocation && (
                              <div className="text-sm text-secondary-500">
                                {match.smartAreaLocation}
                              </div>
                            )}
                            {match.status === 'pending' && (
                              <div className="text-xs text-secondary-400 mt-1">
                                Not yet detected by Smart Area
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <button
                              onClick={() => handleEditTag(match)}
                              disabled={!canEditTag(match)}
                              className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                                canEditTag(match)
                                  ? 'text-primary-600 bg-primary-100 hover:bg-primary-200'
                                  : 'text-secondary-400 bg-secondary-100 cursor-not-allowed'
                              }`}
                            >
                              <PencilIcon className="h-4 w-4 mr-1" />
                              Edit Tag
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden lg:block bg-white shadow overflow-hidden sm:rounded-md">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead className="bg-secondary-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Tag & Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Tag Package Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-200">
                      {filteredMatches.map((match) => {
                        const product = getProductInfo(match.productId);
                        const isRecentlyMatched = recentlyMatchedTags.includes(match.tagNumber);
                        return (
                          <tr key={match.id} className={`${isRecentlyMatched ? 'bg-success-50' : 'hover:bg-secondary-50'}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="mr-3">
                                  {getStatusIcon(match.status)}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-secondary-900 font-mono">
                                    {match.tagNumber}
                                  </div>
                                  <div className="mt-1">
                                    <span className={getStatusBadge(match.status)}>
                                      {match.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-secondary-900">
                                {product?.itemNumber || 'Unknown'}
                              </div>
                              <div className="text-sm text-secondary-500">
                                {product?.description || 'Product not found'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                              {match.quantity}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {match.location ? (
                                <div className="text-sm text-secondary-900">{match.location}</div>
                              ) : (
                                <div className="text-sm text-secondary-500 italic">No location</div>
                              )}
                              {match.smartAreaLocation && (
                                <div className="text-sm text-secondary-500">
                                  {match.smartAreaLocation}
                                </div>
                              )}
                              {match.status === 'pending' && (
                                <div className="text-xs text-secondary-400 mt-1">
                                  Not yet detected by Smart Area
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button
                                onClick={() => handleEditTag(match)}
                                disabled={!canEditTag(match)}
                                className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                                  canEditTag(match)
                                    ? 'text-primary-600 bg-primary-100 hover:bg-primary-200'
                                    : 'text-secondary-400 bg-secondary-100 cursor-not-allowed'
                                }`}
                              >
                                <PencilIcon className="h-4 w-4 mr-1" />
                                Edit Tag
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-secondary-200 sm:px-6 mt-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-secondary-700">
                    Showing{' '}
                    <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalTags)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{totalTags.toLocaleString()}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      if (pageNumber > totalPages) return null;
                      
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNumber === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-secondary-300 text-secondary-500 hover:bg-secondary-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Tag Modal */}
      <EditTagModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingTag(null);
        }}
        onUpdateQuantity={handleUpdateQuantity}
        onUpdateProduct={handleUpdateProduct}
        onClearTag={handleClearTag}
        onReplaceTag={handleReplaceTag}
        tagMatch={editingTag}
        currentProduct={editingTag ? (getProductInfo(editingTag.productId) || null) : null}
        isLoading={isUpdating}
      />
      
      {/* QR Scanner Modal */}
      <SingleTagQRScannerModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onTagConfirmed={handleQRScanConfirmed}
        title="Scan Tag to Search"
      />
      
      {/* Confirmation Modal for successful operations */}
      <ManageTagsConfirmationModal
        isOpen={showConfirmationModal}
        onSearchMore={handleSearchMore}
        onViewUpdatedTag={handleViewUpdatedTag}
        operationDetails={operationDetails}
      />
    </Layout>
  );
};

export default ManageTags;